// 前端全局异常采集 (需求 7146951443)
// 线上“页面没反应 / 白屏”时只有后端 errorId 无法定位, 这里把浏览器侧异常汇入后端 error 日志通道
// 只采集 error 级信息, 不接管 console.log/info/warn, 避免日志量失控
const REPORT_URL = '/api/log/front-error'
const MAX_PER_PAGE_SESSION = 20 // 单页会话上报上限, 防止死循环刷爆日志
const MAX_QUEUE = 10            // 队列满即上报, 不必等定时器
const FLUSH_DELAY = 5000
const MAX_MESSAGE = 500
const MAX_STACK = 1200

let installed = false
let queue = []
let seen = {}
let reported = 0
let timer = null
let sessionId = ''

// 单页会话标识: 同一标签页内复用, 便于把多条异常串成一个用户旅程
function getSessionId() {
  if (sessionId) return sessionId
  try {
    sessionId = sessionStorage.getItem('fe_log_session') || ''
    if (!sessionId) {
      sessionId = 's' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8)
      sessionStorage.setItem('fe_log_session', sessionId)
    }
  } catch (e) {
    sessionId = 's' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8)
  }
  return sessionId
}

// 脱敏: 会话/令牌/手机号/身份证一律不留明文, 避免日志成为新的泄漏点
function sanitize(text, max) {
  if (text === null || text === undefined) return ''
  let s = String(text)
  s = s.replace(/(CUSUT|CUUSERREF|token|authorization)\s*[:=]\s*[^\s&,;]+/gi, '$1=***')
  // \b 必须有: 否则会把时间戳/堆栈里的连续数字误当成手机号或身份证部分掩码, 破坏排查信息
  s = s.replace(/\b1[3-9]\d{9}\b/g, '***')
  s = s.replace(/\b\d{17}[\dXx]\b/g, '***')
  return s.slice(0, max)
}

// 只留路径不留 query: query 里可能带业务参数甚至凭证
function currentPage() {
  try {
    return sanitize(location.pathname, 300)
  } catch (e) {
    return ''
  }
}

function safeJson(v) {
  try {
    return JSON.stringify(v)
  } catch (e) {
    return String(v)
  }
}

function stringifyError(err) {
  if (err instanceof Error) return { message: err.message || err.name || 'Error', stack: err.stack || '' }
  if (typeof err === 'string') return { message: err, stack: '' }
  if (err && typeof err === 'object') return { message: safeJson(err), stack: sanitize(err.stack || '', MAX_STACK) }
  return { message: String(err), stack: '' }
}

function schedule() {
  if (timer) return
  timer = setTimeout(function () {
    timer = null
    flush()
  }, FLUSH_DELAY)
}

function flush() {
  if (timer) {
    clearTimeout(timer)
    timer = null
  }
  if (!queue.length) return
  const errors = queue
  queue = []
  const body = JSON.stringify({ app: 'sudu_web', session: getSessionId(), errors: errors })
  try {
    if (navigator.sendBeacon) {
      // sendBeacon 在页面关闭时也能发出; 返回 false 表示发送队列已满, 退回 fetch
      if (navigator.sendBeacon(REPORT_URL, new Blob([body], { type: 'application/json' }))) return
    }
    fetch(REPORT_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: body,
      keepalive: true
    }).catch(function () { /* 上报失败静默丢弃, 绝不能再触发上报形成死循环 */ })
  } catch (e) {
    /* 同上 */
  }
}

function report(message, stack) {
  try {
    const msg = sanitize(message, MAX_MESSAGE)
    if (!msg) return
    if (reported >= MAX_PER_PAGE_SESSION) return
    const stk = sanitize(stack, MAX_STACK)
    // 同一异常(消息 + 首个堆栈帧)只报一次, 死循环里重复抛不会刷屏
    // traceId 每次请求都不同, 参与 key 会让同一接口的持续失败绕过去重, 因此先剔除
    const keyMsg = msg.replace(/\s*traceId=\S+/g, '')
    const key = keyMsg + '|' + (stk.split('\n')[1] || '')
    if (seen[key]) return
    seen[key] = 1
    reported++
    queue.push({ message: msg, stack: stk, page: currentPage(), time: new Date().toISOString() })
    if (queue.length >= MAX_QUEUE) flush()
    else schedule()
  } catch (e) {
    // 采集自身异常绝不能再抛, 否则形成死循环
  }
}

// installFrontLog 挂载四类异常来源, 在 new Vue 之前调用
export function installFrontLog(Vue) {
  if (installed) return
  installed = true
  const rawConsoleError = console.error

  // 1. Vue 渲染/生命周期/事件里的异常
  if (Vue && Vue.config) {
    Vue.config.errorHandler = function (err, vm, info) {
      const d = stringifyError(err)
      report((info ? '[' + info + '] ' : '') + d.message, d.stack)
      rawConsoleError.call(console, err) // 保留控制台原有可见性, 不影响本地排查
    }
  }

  // 2. 未捕获的 JS 异常; 资源加载失败没有 message/error, 不算 JS 异常, 跳过
  window.addEventListener('error', function (event) {
    if (!event.message && !event.error) return
    const d = stringifyError(event.error)
    report(event.message || d.message, d.stack)
  }, true)

  // 3. 未处理的 Promise 拒绝
  window.addEventListener('unhandledrejection', function (event) {
    const d = stringifyError(event.reason)
    report('unhandledrejection: ' + d.message, d.stack)
  })

  // 4. 业务代码 try/catch 后只打 console.error 的异常 (最常见的一类)
  console.error = function () {
    try {
      const args = Array.prototype.slice.call(arguments)
      let err = null
      const text = args.map(function (a) {
        if (a instanceof Error) {
          err = a
          return a.stack || a.message
        }
        return typeof a === 'object' ? safeJson(a) : String(a)
      }).join(' ')
      report(text, err ? err.stack : '')
    } catch (e) {
      /* 采集失败不影响原始输出 */
    }
    return rawConsoleError.apply(console, arguments)
  }

  // 5. 页面隐藏/关闭前把剩余队列发出去
  window.addEventListener('pagehide', flush)
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') flush()
  })
}
